import { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
    TextField,
    Chip,
} from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import QrCodeIcon from "@mui/icons-material/QrCode";
import WifiOffIcon from "@mui/icons-material/WifiOff";

const PAYMENT_METHODS = [
    { value: "cash", label: "Cash", icon: <PaymentsIcon /> },
    { value: "pos",  label: "POS Card", icon: <CreditCardIcon /> },
    { value: "qr",   label: "QR / E-Wallet", icon: <QrCodeIcon />, onlineOnly: true },
];

const CheckoutDialog = ({ open, cart, onClose, onConfirm, isOnline }) => {
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [note, setNote] = useState("");

    const total = cart.reduce((s, i) => s + i.totalAmount, 0);
    const availableMethods = PAYMENT_METHODS.filter((m) => !m.onlineOnly || isOnline);

    const handleConfirm = () => {
        onConfirm(paymentMethod, note);
        setNote("");
        setPaymentMethod("cash");
    };


    return (
        <Dialog 
            fullWidth 
            maxWidth="xs"
            open={open} 
            onClose={onClose} 
        >
            <DialogTitle fontWeight={600}>Checkout</DialogTitle>
            <DialogContent dividers>
                {/* Order summary */}
                <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    gutterBottom
                >
                    Order Summary
                </Typography>
                <Stack spacing={0.5} mb={2}>
                    {cart.map((item, i) => (
                        <Box 
                            key={i} 
                            sx={{ display: "flex", justifyContent: "space-between" }}
                        >
                            <Typography variant="body2">
                                {item.product.productName} × {item.qty}
                            </Typography>
                            <Typography variant="body2">
                                {item.totalAmount.toLocaleString()}
                            </Typography>
                        </Box>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                            Total
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                            {total.toLocaleString()} VND
                        </Typography>
                    </Box>
                </Stack>

                {/* Payment method */}
                <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    gutterBottom
                >
                    Payment Method
                </Typography>
                {!isOnline && (
                    <Chip
                        icon={<WifiOffIcon fontSize="small" />}
                        label="Offline - QR not available"
                        size="small"
                        color="warning"
                        sx={{ mb: 1 }}
                    />
                )}
                <ToggleButtonGroup
                    fullWidth
                    size="small"
                    exclusive
                    value={paymentMethod}
                    onChange={(_, v) => v && setPaymentMethod(v)}
                    sx={{ mb: 2 }}
                >
                    {availableMethods.map((m) => (
                        <ToggleButton 
                            key={m.value} 
                            value={m.value} 
                            sx={{ flexDirection: "column", py: 1 }}
                        >
                            {m.icon}
                            <Typography variant="caption">
                                {m.label}
                            </Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {/* Note */}
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    label="Note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={handleConfirm}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CheckoutDialog;
